/**
 * Lightens a hex color by a specified amount
 * @param color - The hex color string to lighten
 * @param amount - Amount to lighten (0-1)
 * @returns A lightened hex color string
 */
export const lightenColor = (color: string, amount: number): string => {
  if (!color || !color.startsWith("#")) return "#FFFFFF";
  try {
    // Ensure we have a valid 6-digit hex color
    const hex = color.replace("#", "").padEnd(6, "0");

    // Parse the RGB components
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);

    // Lighten each component
    const lightenComponent = (c: number) =>
      Math.min(255, Math.round(c + (255 - c) * amount));

    const lightR = lightenComponent(r);
    const lightG = lightenComponent(g);
    const lightB = lightenComponent(b);

    // Convert back to hex
    const toHex = (n: number) => {
      const hex = n.toString(16);
      return hex.length === 1 ? "0" + hex : hex;
    };

    return `#${toHex(lightR)}${toHex(lightG)}${toHex(lightB)}`;
  } catch (error) {
    console.error("Error lightening color:", error, "for color:", color);
    return "#FFFFFF";
  }
};

/**
 * Darkens a hex color by a specified amount
 * @param color - The hex color string to darken
 * @param amount - Amount to darken (0-1)
 * @returns A darkened hex color string
 */
export const darkenColor = (color: string, amount: number): string => {
  const hex = color.replace("#", "");
  const rgb = parseInt(hex, 16);
  const r = Math.max(0, ((rgb >> 16) & 0xff) - Math.round(255 * amount));
  const g = Math.max(0, ((rgb >> 8) & 0xff) - Math.round(255 * amount));
  const b = Math.max(0, (rgb & 0xff) - Math.round(255 * amount));
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
};

/**
 * Calculates the relative luminance of a color
 * Used for determining contrast and accessibility
 * 
 * @param color - The hex color string to analyze
 * @returns A luminance value between 0 and 1
 */
export const getLuminance = (color: string): number => {
  const rgb = hexToRgb(color);
  const [r, g, b] = rgb.map((c) => {
    const normalized = c / 255;
    return normalized <= 0.03928
      ? normalized / 12.92
      : Math.pow((normalized + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

/**
 * Picks black or white text for a background.
 * Prefers black when it meets large-text contrast (titles/headers), so mid-bright
 * pinks/oranges stay black instead of flipping to white too early.
 */
export const contrastingTextColor = (background: string): string => {
  const blackRatio = getContrastRatio("#000000", background);
  const whiteRatio = getContrastRatio("#FFFFFF", background);
  if (blackRatio >= 3) return "#000000";
  return whiteRatio >= blackRatio ? "#FFFFFF" : "#000000";
};

export const normalizeHexColor = (color: string): string => {
  const trimmed = color.trim().toLowerCase();
  if (trimmed === "white") return "#ffffff";
  if (trimmed === "black") return "#000000";
  if (/^#[0-9a-f]{3}$/i.test(trimmed)) {
    const [, r, g, b] = trimmed.split("");
    return `#${r}${r}${g}${g}${b}${b}`;
  }
  return trimmed;
};

export const colorsMatch = (a: string, b: string): boolean =>
  normalizeHexColor(a) === normalizeHexColor(b);

/** WCAG contrast ratio between two hex colors. */
export const getContrastRatio = (colorA: string, colorB: string): number => {
  const lighter = Math.max(getLuminance(colorA), getLuminance(colorB));
  const darker = Math.min(getLuminance(colorA), getLuminance(colorB));
  return (lighter + 0.05) / (darker + 0.05);
};

/** Large-text WCAG AA is 3:1; body text is 4.5:1. */
export const hasSufficientContrast = (
  foreground: string,
  background: string,
  minRatio = 3,
): boolean => getContrastRatio(foreground, background) >= minRatio;

/**
 * Converts a hex color string to RGB values
 * @param hex - The hex color string to convert
 * @returns An RGB tuple of numbers
 */
const hexToRgb = (hex: string): [number, number, number] => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? [
        parseInt(result[1], 16),
        parseInt(result[2], 16),
        parseInt(result[3], 16),
      ]
    : [0, 0, 0];
};

/**
 * Determines whether black or white text would be more readable on a background color
 * Uses YIQ formula for contrast calculation
 * 
 * @param hexcolor - The hex color string of the background
 * @returns "black" or "white" depending on which provides better contrast
 */
export const getContrastYIQ = (hexcolor: string) => {
  const r = parseInt(hexcolor.slice(1, 3), 16);
  const g = parseInt(hexcolor.slice(3, 5), 16);
  const b = parseInt(hexcolor.slice(5, 7), 16);
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 128 ? "black" : "white";
};
